import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import { router } from './router'
import './style.css'
import { startMediaWorker } from './lib/media'
import { initTheme } from './lib/theme'
import { applyFonts } from './lib/fonts'
import type { InstanceInfo } from './api/types'
import { watch } from 'vue'
import { apiUrl, appInvoke, deepLinkTarget, hasServer, isApp, setServer } from './platform'
import { useUnread } from './stores/unread'
import { nativePush } from './lib/push'
import { updateBeforeOpening } from './lib/appUpdate'

initTheme()

// The server's fonts, before anyone signs in (sign-in screens use them too).
if (hasServer()) {
  void fetch(apiUrl('/instance'))
    .then((r) => (r.ok ? (r.json() as Promise<InstanceInfo>) : null))
    .then((i) => i && applyFonts(i.fonts))
    .catch(() => undefined)
}

const app = createApp(App).use(createPinia()).use(router)
// The apps first install a newer release, if there is one (sideloaded builds).
const root = document.getElementById('app')!
void (isApp ? updateBeforeOpening(root) : Promise.resolve()).finally(() => app.mount(root))

if (isApp) {
  // hrth:// links queue up in the shell until the page takes them (on load, when nudged, and
  // when it comes back on screen).
  const takeLinks = async (): Promise<void> => {
    // A tapped notification (pulled, so one tapped while this page was starting isn't lost).
    const open = await nativePush.takeOpen().catch(() => null)
    if (open?.startsWith('/')) await router.push(open)
    for (const link of await appInvoke<string[]>('take_links')) {
      const to = deepLinkTarget(link)
      if (!to) continue
      if (to.server) {
        setServer(to.server)
        window.location.replace(to.path)
        return
      }
      await router.push(to.path)
    }
  }
  ;(window as unknown as { hrthTakeLinks: () => void }).hrthTakeLinks = () => void takeLinks()
  void router.isReady().then(takeLinks)
  document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && void takeLinks())
  watch(
    () => useUnread().total,
    (count) => void appInvoke('set_unread', { count }).catch(() => undefined),
    { immediate: true },
  )
}

// Streams encrypted audio/video and shows push notifications; harmless where unsupported.
void startMediaWorker()
// A tapped notification asks an open window to show its conversation.
navigator.serviceWorker?.addEventListener('message', (e: MessageEvent) => {
  const d = e.data as { type?: string; url?: string } | null
  if (d?.type === 'hrth-open' && d.url?.startsWith('/')) void router.push(d.url)
})
